import React, { useCallback, useEffect } from 'react';
import { translate } from '@shakkerai/util-i18n';
import { GenerateButton, NumberInputGroup } from '@shakkerai/pro-components';
import { inpaintPresetModels, presetCheckpointsMap } from '@shakkerai/pro-models';
import { MODEL_FLUX, MODEL_SD15, MODEL_SDXL, SD3_MODELS_FAMILY } from '@shakkerai/pro-models';
import { updateVipState, useVipState } from '@shakkerai/pro-store';
import { useUserIdentificationHooks } from '@shakkerai/pro-commercial';
import { getModelVersionDetail, isMainland } from '@shakkerai/pro-utils';
import { useTempState } from '@starflow/app/store';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { GeneratorPanelFooter, GenerateCount, GenerateSize } from '@starflow/app/modules/generator/components/_legacy';
import { Models, Reference, AddOns, BaseParams, Prompt } from '@starflow/app/modules/generator/components/_legacy';
import { scheduleTaskForUnit } from '@starflow/app/helpers/process';
import { vipOptions } from '../../../generator/components/_legacy/count';

const Preparing = () => {
  return <div />;
};

const ModelPreset = React.memo(({ value, disabled, allowUseOriginal, onChange }) => {
  return (
    <div className="grid grid-cols-3 gap-1">
      {inpaintPresetModels.map((item) => {
        if (item.key === 'original' && !allowUseOriginal) {
          return null;
        }

        const selected = item.key === value;

        return (
          <button
            key={item.key}
            disabled={selected}
            onClick={() => onChange(item)}
            className={`${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${
              selected ? 'bg-primary/10 border-primary text-[#005848]' : 'bg-[#F4F6F8] border-white'
            } text-xs text-center h-7 rounded-sm text-[#2D2D2D] border`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
});

export const InpaintPanel = React.memo(({ element, preparing, generator }) => {
  const isSimple = useTempState('globalMode') === 'simple';

  const { baseType, segments, modelPreset: prevPreset, count } = generator.data;

  const isFluxModel = baseType === MODEL_FLUX;
  const baseTypeSupported = isFluxModel || !SD3_MODELS_FAMILY[baseType];
  const cnEnabled = !isSimple && (baseType === MODEL_SDXL || baseType === MODEL_SD15);

  const { userVipInfo } = useVipState();
  const { isOrdinaryUser } = useUserIdentificationHooks(userVipInfo);

  const isPending = false;

  const handleChange = useCallback(
    (updatedData) => {
      updateUnitData(generator.id, updatedData);
    },
    [generator.id],
  );

  const handleGenerate = useCallback(() => {
    if (!isMainland && vipOptions.includes(count) && isOrdinaryUser) {
      // 用户不是会员，则弹出付费提示
      updateVipState({
        proPurchaseModalVisible: true,
        currentErrorCode: 1200000148,
        currentErrorMsg: 'You have exceeded the image limit for free users. Please upgrade for unlimited access.',
      });

      return;
    }

    scheduleTaskForUnit(generator.id);
  }, [generator.id, count]);

  const handleSwitchModelPreset = useCallback(
    async (presetData) => {
      const updatedData = { modelPreset: presetData.key };

      if (presetData.key === 'custom' || presetData.key === 'original') {
        try {
          const params = presetData.key === 'custom' ? generator.data : generator.data.originalParams || generator.data;
          const { presetBaseModelId, checkpoint } = params;

          let versionUuid = null;

          if (presetBaseModelId) {
            versionUuid = presetCheckpointsMap[presetBaseModelId]?.versionDetail?.uuid;
          } else if (checkpoint) {
            versionUuid = checkpoint.uuid;
          }

          if (versionUuid) {
            const [, versionIntro, baseType] = await getModelVersionDetail(versionUuid);
            updatedData.baseType = baseType;

            if (versionIntro.samplerMethod) updatedData.samplerMethod = versionIntro.samplerMethod;
            if (versionIntro.samplerMethods?.[0] >= 0)
              updatedData.samplerMethod = String(versionIntro.samplerMethods[0]);

            if (versionIntro.cfgScale) updatedData.cfgScale = versionIntro.cfgScale;
            if (versionIntro.cfg) updatedData.cfgScale = versionIntro.cfg;
          }

          console.log(`[DEBUG] params`, params);
        } catch (error) {
          console.log(`[DEBUG] error`, error);
        }
      } else {
        updatedData.baseType = MODEL_SDXL;
        updatedData.cfgScale = 7;
        updatedData.vae = presetData.vaeValue;
        updatedData.samplerMethod = presetData.sampleMethodValue;
      }

      updateUnitData(generator.id, updatedData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prevPreset, generator.id],
  );

  const handleChangeStrength = useCallback(
    (strength) => {
      updateUnitData(generator.id, { strength });
    },
    [generator.id],
  );

  useEffect(() => {
    updateUnitData(generator.id, { didMount: true });
    return () => {
      updateUnitData(generator.id, { didMount: null });
    };
  }, [generator.data.modelPreset]);

  if (preparing) {
    return (
      <div id="inpaint-generator-panel" className="py-4 px-3 flex-1 overflow-auto tiny-scrollbar">
        <Preparing preparing={preparing} imageId={element.id} />
      </div>
    );
  }

  const modelPreset = generator.data.modelPreset;
  const originalBaseType = generator.data.originalBaseType;
  const hasSelections = segments?.selections?.length;
  const allowInpaint = !!(modelPreset && hasSelections);
  const allowUseOriginal =
    element?.innerTaskId && (originalBaseType === MODEL_FLUX || !SD3_MODELS_FAMILY[originalBaseType || MODEL_SDXL]);

  let toolTip = null;

  if (!hasSelections) {
    toolTip = translate('imagetool_inpaint_choose_area_tip');
  } else if (!modelPreset) {
    toolTip = translate('imagetool_inpaint_choose_style_tip');
  }

  return (
    <>
      <h2 className="flex items-center px-4 h-[44px] border-b border-[#efefef] font-bold text-sm text-[#2d2d2d] font-grotesk">
        {translate('imagetool_inpaint')}
      </h2>
      <div id="inpaint-generator-panel" className="pt-4 px-4 pb-1 flex-1 overflow-auto tiny-scrollbar">
        <Prompt isFull={false} generator={generator} onChange={handleChange} />
        {!isSimple && <div className="-mx-4 my-[12px] border-t border-[#efefef]" />}
        {!isSimple && (
          <h5 className="flex-1 mb-[12px] text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
            {translate('imagetool_inpiant_style')}
          </h5>
        )}
        {!isSimple && (
          <ModelPreset value={modelPreset} allowUseOriginal={allowUseOriginal} onChange={handleSwitchModelPreset} />
        )}
        {modelPreset === 'custom' && !isSimple && (
          <>
            <div className="-mx-4 my-3.5 border-t border-[#efefef]" />
            <Models generator={generator} />
          </>
        )}
        <div className="-mx-4 my-[12px] border-t border-[#efefef]" />
        <div className="flex flex-col items-stretch">
          <span className="mr-[12px] text-xs text-[#383838] font-bold">{translate('inpaint_scale')}</span>
          {/* 重绘幅度 */}
          <div className="flex flex-1 items-center translate-y-[1px]">
            <NumberInputGroup
              suffix="%"
              name="strength"
              inputWidth={40}
              value={isFluxModel ? 100 : generator.data.strength}
              onChange={handleChangeStrength}
              // Flux 每次拖动加10 其他的都是1
              step={isFluxModel ? 10 : 1}
            />
          </div>
        </div>
        {cnEnabled && (
          <>
            <div className="-mx-4 my-[12px] border-t border-[#efefef]" />
            <Reference generator={generator} />
          </>
        )}
        <div className="-mx-4 my-[12px] border-t border-[#efefef]" />
        <h5 className="flex-1 text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">{translate('setting_size')}</h5>
        {/* 生图尺寸 */}
        <GenerateSize disabled generator={generator} />
        <div className="-mx-4 my-[12px] border-t border-[#efefef]" />
        <h5 className="flex-1 text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
          {translate('setting_imgnum')}
        </h5>
        {/* 生图数量 */}
        <GenerateCount generator={generator} />
        {!isSimple && (
          <>
            <div className="-mx-4 my-[12px] border-t border-[#efefef]" />
            <BaseParams generator={generator} />
            {cnEnabled && (
              <>
                <div className="-mx-4 my-[12px] border-t border-[#efefef]" />
                <AddOns generator={generator} />
              </>
            )}
          </>
        )}
      </div>
      <GeneratorPanelFooter generator={generator}>
        <GenerateButton
          disabled={!allowInpaint || isPending || !baseTypeSupported}
          tooltip={toolTip}
          onClick={handleGenerate}
        >
          {translate(isPending ? 'generator_pending' : 'imagetool_inpaint')}
        </GenerateButton>
      </GeneratorPanelFooter>
    </>
  );
});
